import {
  type ChangeEvent,
  type DragEvent,
  type ReactElement,
  useCallback,
  useRef,
} from 'react'
import { Loader2, Upload } from 'lucide-react'
import { BOM_IMPORT_FILE_ACCEPT } from '@/shared/lib/bomImportFileTypes'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'

export type BomImportDropZoneLabels = {
  formatsRequirement: string
  dragDrop: string
  orClick: string
  selectFile: string
}

type BomImportDropZoneProps = {
  isBusy: boolean
  busyLabel?: string
  fileError: string | null
  labels: BomImportDropZoneLabels
  onFileSelected: (file: File) => void
  accept?: string
  showFormats?: boolean
}

export function BomImportDropZone({
  isBusy,
  busyLabel,
  fileError,
  labels,
  onFileSelected,
  accept = BOM_IMPORT_FILE_ACCEPT,
  showFormats = true,
}: BomImportDropZoneProps): ReactElement {
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFiles = useCallback(
    (files: File[]) => {
      const file = files[0]
      if (file == null) {
        return
      }
      onFileSelected(file)
    },
    [onFileSelected]
  )

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const fileList = event.target.files
    if (fileList != null && fileList.length > 0) {
      handleFiles(Array.from(fileList))
    }
    event.target.value = ''
  }

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      const fileList = event.dataTransfer.files
      if (fileList.length > 0) {
        handleFiles(Array.from(fileList))
      }
    },
    [handleFiles]
  )

  const showBusyStatus = isBusy && busyLabel != null

  return (
    <div className='space-y-2'>
      {fileError != null && (
        <p className='text-sm text-destructive' role='alert'>
          {fileError}
        </p>
      )}
      {showFormats && (
        <p className='text-sm text-muted-foreground'>
          {labels.formatsRequirement}
        </p>
      )}
      <div
        className={cn(
          'flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center',
          'border-muted-foreground/25 hover:border-muted-foreground/50',
          isBusy && 'pointer-events-none',
          isBusy && !showBusyStatus && 'opacity-60'
        )}
        aria-busy={isBusy}
        onDragOver={(event) => {
          event.preventDefault()
        }}
        onDrop={handleDrop}
      >
        {showBusyStatus && (
          <>
            <Loader2
              className='mb-3 size-10 animate-spin text-muted-foreground'
              aria-hidden
            />
            <p className='text-sm text-muted-foreground' role='status'>
              {busyLabel}
            </p>
          </>
        )}
        {!showBusyStatus && (
          <Upload className='mb-2 size-10 text-muted-foreground' aria-hidden />
        )}
        {!showBusyStatus && (
          <p className='mb-3 text-sm text-muted-foreground'>
            {labels.dragDrop}
          </p>
        )}
        {!showBusyStatus && (
          <p className='mb-4 text-xs text-muted-foreground'>{labels.orClick}</p>
        )}
        <input
          ref={fileInputRef}
          accept={accept}
          className='sr-only'
          disabled={isBusy}
          onChange={handleFileChange}
          type='file'
        />
        {!showBusyStatus && (
          <Button
            disabled={isBusy}
            size='sm'
            type='button'
            variant='secondary'
            onClick={() => {
              fileInputRef.current?.click()
            }}
          >
            {labels.selectFile}
          </Button>
        )}
      </div>
    </div>
  )
}
